/**
 * L'ONGLET MAINTENANCE D'UN APPAREIL, côté écran — module PUR (spec 002,
 * récit 1 et récit 7, 03/11/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ce que sert `GET ged/maintenances/equipement/:code`, et ce que l'écran en
 * dit. Le serveur CALCULE (verdict, retards, prochaine échéance — un seul
 * calcul pour tout Nafis, `etatMaintenanceAppareil`) ; ce fichier ne fait que
 * le mettre en mots, et lire l'adresse de l'onglet. Il est pur pour être testé
 * sans écran (`front-maintenance-appareil`).
 *
 * ⚠️ AUCUN CALCUL DE DATE ICI. La prochaine échéance se LIT dans `etat`, jamais
 * ne se refait — c'est tout l'objet du récit 7 (trois calculs divergents
 * avant lui). Le cliquet `qualite-maintenance-cliquet` y veille.
 */
import type { EtatMaintenance, MaintenanceLigne, MaintenanceOccurrence, GedPieceEquip } from './types';
import type { EntreeHistorique } from './historique-maintenance';

export type GenreIntervention = 'preventive' | 'hors_plan' | 'curative' | 'metrologie';

export const LIBELLE_GENRE: Record<GenreIntervention, string> = {
  preventive: 'Maintenance préventive',
  hors_plan: 'Hors plan',
  curative: 'Curative',
  metrologie: 'Métrologie',
};

export type EntreeAppareil = EntreeHistorique & { genre: GenreIntervention };

/** `sertA` : libellés des lignes ACTIVES qui citent le support ; `commun` :
 *  pièce reliée à aucune ligne, proposée à toutes les maintenances (récit 2). */
export type SupportAppareil =
  | { type: 'piece'; cle: string; nature: string; libelle: string; piece: GedPieceEquip; sertA: string[]; commun?: boolean }
  | { type: 'document'; cle: string; nature: 'document'; code: string; libelle: string; version: string; statut: string; sertA: string[]; commun?: boolean };

/** Ce que sert `GET ged/maintenances/equipement/:code`. */
export interface MaintenanceAppareilReponse {
  aujourdhui: string;
  appareil: { documentId: string; code: string; nom: string; statut: string; nature: string | null; categorie: string };
  etat: EtatMaintenance | null;
  /** Échéances du plan NON faites, retards d'abord. */
  echeances: MaintenanceOccurrence[];
  lignes: MaintenanceLigne[];
  historique: EntreeAppareil[];
  supports: SupportAppareil[];
  postes: { code: string; libelle: string }[];
  tronque: boolean;
}

/** Le verdict en clair, pour l'en-tête du dossier et l'onglet. */
export function libelleVerdict(etat: Pick<EtatMaintenance, 'verdict' | 'retards'> | null | undefined): string {
  if (!etat) return 'état inconnu';
  switch (etat.verdict) {
    case 'a_jour': return 'À jour';
    case 'en_retard': return `${etat.retards} en retard`;
    case 'hors_service': return 'Hors service';
    case 'reforme': return 'Réformé';
    case 'sans_plan': return 'Sans plan';
    case 'sans_objet': return 'Aucun plan attendu';
    default: return 'état inconnu';
  }
}

/** Le ton de la pastille du verdict (`TONES` de `meta.ts`). */
export function tonVerdict(etat: Pick<EtatMaintenance, 'verdict'> | null | undefined): 'success' | 'danger' | 'warning' | 'muted' {
  switch (etat?.verdict) {
    case 'a_jour': return 'success';
    case 'en_retard': return 'danger';
    case 'hors_service': case 'sans_plan': return 'warning';
    default: return 'muted';
  }
}

/** « maintenance » / « métrologie », avec ce qui est dû : ce que dit la prochaine échéance. */
export function natureProchaine(etat: EtatMaintenance | null | undefined): string {
  const p = etat?.prochaine;
  if (!p) return '';
  if (p.nature === 'metrologie') return p.source === 'posee' ? 'métrologie (date posée à la main)' : 'métrologie';
  return p.libelle ? `maintenance — ${p.libelle}` : 'maintenance';
}

/* ── L'adresse de l'onglet ──────────────────────────────────────────────────
 *
 *  `?onglet=maintenance&ref=<ligne>@<date>`      → la fenêtre « Faite » ouverte
 *  `?onglet=maintenance&intervention=<id>`        → le détail d'une intervention
 *
 * `<ligne>` est la RÉFÉRENCE de la ligne de plan (`BIO24-MNT-…`), par la règle
 * d'adressage (jamais un identifiant interne). Le centre des tâches pose encore
 * `<documentId>@<date>` : les deux sont acceptés à la lecture. */
export interface CibleMaintenance { ref?: string; intervention?: string }

export function lireCibleMaintenance(search: string): CibleMaintenance {
  const q = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const out: CibleMaintenance = {};
  const ref = (q.get('ref') || '').trim();
  const iv = (q.get('intervention') || '').trim();
  if (ref && ref.includes('@')) out.ref = ref;
  if (iv) out.intervention = iv;
  return out;
}

export function cheminMaintenanceAppareil(code: string, cible: CibleMaintenance = {}): string {
  const q = new URLSearchParams({ onglet: 'maintenance' });
  if (cible.ref) q.set('ref', cible.ref);
  if (cible.intervention) q.set('intervention', cible.intervention);
  return `/equipements/${encodeURIComponent(code)}?${q.toString()}`;
}

/** L'échéance désignée par `<ligne>@<date>` (référence ou identifiant de la ligne). */
export function echeanceDesignee<T extends Pick<MaintenanceOccurrence, 'maintenanceRef' | 'planId' | 'date'>>(
  echeances: T[], ref: string | undefined,
): T | null {
  if (!ref || !ref.includes('@')) return null;
  const i = ref.lastIndexOf('@');
  const ligne = ref.slice(0, i);
  const date = ref.slice(i + 1);
  return echeances.find((o) => o.date === date && (o.maintenanceRef === ligne || o.planId === ligne)) || null;
}

/** L'identifiant d'une intervention dans l'adresse : celui de la fiche de vie,
 *  de l'acte du plan, ou la référence d'une vérification. */
export function idIntervention(e: Pick<EntreeHistorique, 'ivId' | 'acteId' | 'reference' | 'cle' | 'origine'>): string {
  if (e.origine === 'fiche' && e.ivId) return e.ivId;
  if (e.origine === 'plan' && e.acteId) return e.acteId;
  return e.reference || e.cle;
}

export function interventionDesignee<T extends Pick<EntreeHistorique, 'ivId' | 'acteId' | 'reference' | 'cle' | 'origine'>>(
  historique: T[], id: string | undefined,
): T | null {
  if (!id) return null;
  return historique.find((e) => idIntervention(e) === id || e.cle === id) || null;
}
